"use client";

import {
  getConversationLastMessagePreview,
  type UIConversation,
} from "@/actions/conversations/conversations.type";
import { seedConversationCache } from "@/app/(home)/chat/conversation/seed-conversation-cache";
import { Avatar } from "@/components/avatar";
import { DateFromNow } from "@/components/date-from-now";
import { NavLink } from "@/components/ui/nav-link";
import { Skeleton } from "@/components/ui/skeleton";
import { AppRoutes, NavigationConfig } from "@/config/navigation-config";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useCallback, useState } from "react";
import { useConversationId } from "../../../conversation/hooks/use-conversation-id";
import { useConversationsMenu } from "../conversations-layout-provider";

dayjs.extend(relativeTime);

interface ConversationsMenuItemProps {
  conversation: UIConversation;
  blurred?: boolean;
}

export function ConversationsMenuItem({
  conversation,
  blurred,
}: ConversationsMenuItemProps) {
  const [conversationId] = useConversationId();
  const { isMobile, close } = useConversationsMenu();
  const queryClient = useQueryClient();

  const seedConversation = useCallback(() => {
    seedConversationCache(queryClient, conversation);
  }, [conversation, queryClient]);

  const lastMessagePreview = getConversationLastMessagePreview(
    conversation.last_message,
  );

  return (
    <NavLink
      href={NavigationConfig.getPath(AppRoutes.CHATS_VIEW, {
        chatId: conversation.id,
      })}
      isActive={conversationId === conversation.id}
      onMouseEnter={seedConversation}
      className={({ isActive }) =>
        cn(
          "hover:bg-accent relative block min-w-0 cursor-pointer border-l-4 border-transparent p-4",
          {
            "bg-accent/50 border-pink-500": isActive,
            "after:pointer-events-none after:absolute after:inset-0 after:backdrop-blur-[2px] after:content-['']":
              blurred,
          },
        )
      }
      onClick={() => {
        seedConversation();
        if (isMobile) {
          close();
        }
      }}
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="relative">
          <Avatar
            src={conversation.character.bio?.avatar_url}
            fallback={conversation.character.name}
            className="size-10"
          />
          <span className="border-background absolute right-0 bottom-0 h-3 w-3 rounded-full border-2 bg-green-500" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              {conversation.character.name}
            </h3>
            {conversation.last_message?.id &&
              conversation.last_message.created_at && (
              <DateFromNow
                className="text-muted-foreground text-xs"
                date={conversation.last_message.created_at}
              />
            )}
          </div>
          <p className="text-muted-foreground truncate text-sm">
            {lastMessagePreview}
          </p>
        </div>
      </div>
    </NavLink>
  );
}

export function ConversationsMenuItemSkeleton() {
  const [widths] = useState(() => {
    const firstLine = Math.random() * 0.3 + 0.7; // 70-100%
    const secondLine = Math.random() * 0.4 + 0.4; // 40-80%
    return {
      first: `${(firstLine * 100).toFixed(0)}%`,
      second: `${(secondLine * 100).toFixed(0)}%`,
    };
  });

  return (
    <div className="min-w-0 border-l-4 border-transparent p-4">
      <div className="flex min-w-0 items-start gap-3">
        <div className="relative">
          <Skeleton className="h-12 w-12 rounded-full bg-zinc-300" />
          <Skeleton className="border-background absolute right-0 bottom-0 h-3 w-3 rounded-full border-2 bg-zinc-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between">
            <Skeleton className="h-4 w-24 bg-zinc-300" />
            <Skeleton className="h-3 w-12 bg-zinc-300" />
          </div>
          <div className="space-y-1">
            <Skeleton
              className="h-3.5 bg-zinc-300"
              style={{ width: widths.first }}
            />
            <Skeleton
              className="h-3.5 bg-zinc-300"
              style={{ width: widths.second }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
